import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "./ui/Button";
import { PRODUCTS } from "../constants";
import {
  ArrowRight,
  Download,
  Headphones,
  Shield,
  Droplets,
  Bug,
  Layers,
  Ruler,
  Flame,
  Wind,
  Hammer,
  Award,
  Building2,
  Leaf,
  Home,
} from "lucide-react";
import { FileText, ClipboardList, Zap, RefreshCw, Box } from "lucide-react";

// Icon for technical spec by label (match on lowercase key)
const getSpecIcon = (label: string): React.ComponentType<any> => {
  const key = label.toLowerCase();
  if (key.includes("grade") || key.includes("standard") || key.includes("certification")) return Award;
  if (key.includes("species") || key.includes("core") || key.includes("face veneer") || key.includes("variants")) return Layers;
  if (key.includes("size") || key.includes("thickness") || key.includes("calibration") || key.includes("surface finish") || key.includes("modulus")) return Ruler;
  if (key.includes("glue") || key.includes("adhesive") || key.includes("moisture")) return Droplets;
  if (key.includes("density")) return Box;
  if (key.includes("performance")) return Zap;
  if (key.includes("reusability")) return RefreshCw;
  return ClipboardList;
};

// Icon mapping for features
const iconMap: { [key: string]: React.ComponentType<any> } = {
  Ruler,
  Bug,
  Flame,
  Shield,
  Wind,
  Hammer,
  Droplets,
  Layers,
  Award,
  Building2,
  Leaf,
  Home,
};

export const IndividualProductPage: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();

  // Find product by slug
  const product = PRODUCTS.find((p) => p.id === slug);

  // If product not found, redirect or show error
  if (!product) {
    return (
      <div className="pt-32 pb-24 bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-heading-section font-open-sans-hebrew font-bold mb-4">
            Product Not Found
          </h1>
          <Button onClick={() => navigate("/products")}>
            Back to Products
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 bg-white min-h-screen">
      <div className="container mx-auto px-6 space-y-16 font-raleway max-w-full lg:max-w-[70%]">
        {/* Top Section: left = title, description, Available In; right = image (same height as left) */}
        <div className="flex flex-col lg:flex-row gap-12 lg:items-stretch">
            {/* Left Content */}
            <div className="flex-1 pt-4 flex flex-col">
              <div className="flex gap-3 mb-6">
                {product.grade && (
                  <span className="px-3 py-1 bg-gray-100 text-[10px] font-bold uppercase tracking-wider text-gray-600 rounded-sm">
                    {product.grade}
                  </span>
                )}
                {product.certification && (
                  <span className="px-3 py-1 bg-orange-50 text-[10px] font-bold uppercase tracking-wider text-orange-600 rounded-sm flex items-center gap-1 border border-orange-100">
                    <Flame size={10} className="fill-orange-600" />{" "}
                    {product.certification}
                  </span>
                )}
              </div>
              <h1
                className="text-heading-large font-open-sans-hebrew font-bold bg-gradient-to-r
         from-[#e20016] from-[0%]
         via-[#712f59] via-[40%]
         to-[#005d9c] to-[100%] bg-clip-text text-transparent mb-6"
              >
                {product.name} {product.subtitle && `(${product.subtitle})`}
              </h1>
              <p className="text-gray-500 text-body mb-12 max-w-xl">
                {product.description}
              </p>

              {product.availableIn && (
                <div className="mb-8">
                  <h4
                    className="text-card-title font-medium bg-gradient-to-r
         from-[#e20016] from-[0%]
         via-[#712f59] via-[40%]
         to-[#005d9c] to-[100%] bg-clip-text text-transparent text-[24px] mb-3"
                  >
                    Available In
                  </h4>
                  <div className="flex flex-col flex-wrap gap-x-10 gap-y-4">
                    <div>
                      <span className="text-body text-gray-500 font-medium block mb-px">
                        Thickness
                      </span>
                      <ul className="flex flex-wrap items-center gap-x-6 gap-y-2 list-none p-0 m-0 text-body text-gray-800 font-medium">
                        {product.availableIn.thickness.map((t, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-800 shrink-0" aria-hidden />
                            {t}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <span className="text-body text-gray-500 font-medium block mb-px">
                        Size
                      </span>
                      <ul className="flex flex-wrap items-center gap-x-6 gap-y-2 list-none p-0 m-0 text-body text-gray-800 font-medium">
                        {product.availableIn.size.map((s, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-800 shrink-0" aria-hidden />
                            {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                    {product.availableIn.variants && product.availableIn.variants.length > 0 && (
                      <div>
                        <span className="text-body text-gray-500 font-medium block mb-px">
                          Variants
                        </span>
                        <ul className="flex flex-wrap items-center gap-x-6 gap-y-2 list-none p-0 m-0 text-body text-gray-800 font-medium">
                          {product.availableIn.variants.map((v, i) => (
                            <li key={i} className="flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-gray-800 shrink-0" aria-hidden />
                              {v}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right Image - same height as left content on lg */}
            <div className="lg:w-[55%] flex">
              <div className="rounded-[24px] overflow-hidden h-[300px] lg:h-full lg:min-h-[600px] lg:max-h-[620px] w-full relative flex-1">
                <img
                  src={product.image}
                  className="absolute inset-0 w-full h-full object-fill"
                  alt={product.name}
                />
              </div>
            </div>
          </div>

        {/* Specs & Sidebar */}
        <div className="flex flex-col lg:flex-row gap-20">
            {/* Specs Table */}
            <div className="flex-1">
              <h3
                className="text-heading-section font-open-sans-hebrew font-bold bg-gradient-to-r
         from-[#e20016] from-[0%]
         via-[#712f59] via-[40%]
         to-[#005d9c] to-[100%] bg-clip-text text-transparent mb-8"
              >
                Technical Specifications
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
                {(product.technicalSpecs || []).map((row, i) => {
                  const SpecIcon = getSpecIcon(row.label);
                  return (
                  <div key={i} className="flex gap-3 p-0">
                    <div className="w-12 h-12 shrink-0 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600">
                      <SpecIcon size={28} strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-body font-open-sans-hebrew font-bold text-gray-900 uppercase tracking-wide mb-1">
                        {row.label}
                      </div>
                      <div className="text-xs text-gray-600">
                        {row.value}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:w-[400px] space-y-8">
              <div>
                <h3
                  className="text-heading-section font-open-sans-hebrew font-bold bg-gradient-to-r
         from-[#e20016] from-[0%]
         via-[#712f59] via-[40%]
         to-[#005d9c] to-[100%] bg-clip-text text-transparent mb-8"
                >
                  Warranty & Compliance
                </h3>

                <div className="space-y-4 bg-[#fafafa] rounded-lg p-4">
                  <div className="rounded-lg border-2 border-gray-200 p-5 flex items-center justify-between hover:shadow-sm transition-shadow cursor-pointer bg-[#FAFAFA]">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center text-red-600 border border-red-100">
                        <FileText size={18} />
                      </div>
                      <div>
                        <h4 className="text-card-title font-open-sans-hebrew font-bold text-gray-900">
                          Technical Data Sheet
                        </h4>
                        <p className="text-body text-gray-500 mt-1">
                          PDF • 1.2 MB
                        </p>
                      </div>
                    </div>
                    <Download size={16} className="text-gray-400" />
                  </div>

                  <a
                    href={`/view-pdf?file=${encodeURIComponent("/assets/pdfs/croma-brochure.pdf")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-lg border-2 border-gray-200 p-5 flex items-center justify-between hover:shadow-sm transition-shadow cursor-pointer bg-[#FAFAFA]"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center text-green-600 border border-green-100">
                        <FileText size={18} />
                      </div>
                      <div>
                        <h4 className="text-card-title font-open-sans-hebrew font-bold text-gray-900">
                          Full Product Brochure
                        </h4>
                        <p className="text-body text-gray-500 mt-1">
                          PDF • Download
                        </p>
                      </div>
                    </div>
                    <Download size={16} className="text-gray-400" />
                  </a>
                </div>
              </div>

              <div className="pt-4">
                <h3
                  className="text-heading-section font-open-sans-hebrew font-bold bg-gradient-to-r
         from-[#e20016] from-[0%]
         via-[#712f59] via-[40%]
         to-[#005d9c] to-[100%] bg-clip-text text-transparent mb-6"
                >
                  Technical Support
                </h3>
                <div className="bg-[#FAFAFA] rounded-xl p-8 border border-gray-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center text-white border border-gray-200">
                      <Headphones size={16} />
                    </div>
                    <span className="font-open-sans-hebrew font-bold text-body text-gray-900">
                      Expert Technical Support
                    </span>
                  </div>
                  <p className="text-body text-gray-500 mb-6">
                    Our support team is available to assist you with detailed
                    specifications, installation advice, and product-related
                    questions for smooth, hassle-free project execution.
                  </p>
                  <button className="w-full py-3.5 bg-[#EBEBEB] hover:bg-black hover:text-white transition-colors rounded-lg text-body font-open-sans-hebrew font-bold uppercase tracking-wider flex items-center justify-center gap-2 text-gray-900">
                    Contact Technical Team <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            </div>
          </div>

        {/* USP & Features Grid */}
        <div className="bg-[#FAFAFA] rounded-[40px] py-8 lg:py-20 px-2 lg:px-12">
            <div className="flex flex-col items-center text-center mb-12">
              <h2
                className="text-heading-large font-open-sans-hebrew font-bold bg-gradient-to-r
                    from-[#e20016] from-[0%]
                    via-[#712f59] via-[40%]
                    to-[#005d9c] to-[100%] bg-clip-text text-transparent mb-4"
              >
                USP & Features
              </h2>
              <p className="text-gray-500 text-body max-w-xl">
                Key differentiators and construction details for consistent
                strength and performance.
              </p>
            </div>

            {/* {(product.usp && product.usp.length > 0) && (
              <div className="mb-16">
                <h3 className="text-heading-sub font-bold text-gray-900 mb-6">Unique Selling Propositions</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {product.usp.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100">
                      <span className="w-2 h-2 rounded-full bg-croma-red shrink-0" />
                      <span className="text-body text-gray-800 font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )} */}

            <div>
              {/* <h3 className="text-heading-sub font-bold text-gray-900 mb-6">Features</h3> */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-16">
                {(product.features || []).map((feature, i) => {
                  const IconComponent = iconMap[feature.icon] || Shield;
                  return (
                    <div key={i} className="flex flex-col gap-6 items-start">
                      <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-gray-900 border border-gray-100">
                        <IconComponent size={22} strokeWidth={1} />
                      </div>
                      <div>
                        <h4
                          className="text-heading-sub font-open-sans-hebrew font-bold bg-gradient-to-r
         from-[#e20016] from-[0%]
         via-[#712f59] via-[40%]
         to-[#005d9c] to-[100%] bg-clip-text text-transparent mb-3"
                        >
                          {feature.title}
                        </h4>
                        <p className="text-body text-gray-500">
                          {feature.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

        {/* Applications */}
        <div className="mb-32">
            <div className="flex flex-col items-center mb-16 text-center">
              <span className="px-3 py-1 bg-[#FAFAFA] text-[10px] font-open-sans-hebrew font-bold uppercase tracking-wider text-gray-500 rounded-full mb-6 border border-gray-100 flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>{" "}
                USE CASES
              </span>
              <h2
                className="text-heading-large font-open-sans-hebrew font-bold bg-gradient-to-r
         from-[#e20016] from-[0%]
         via-[#712f59] via-[40%]
         to-[#005d9c] to-[100%] bg-clip-text text-transparent"
              >
                Applications Designed For
                <br />
                Every Space
              </h2>
              <p className="text-gray-500 text-body mt-6">
                Discover where Croma Ply delivers exceptional performance.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {(product.applications || []).map((app, i) => (
                <div
                  key={i}
                  className="group relative h-[500px] rounded-[24px] overflow-hidden cursor-pointer"
                >
                  <img
                    src={app.img}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    alt={app.title}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-90"></div>
                  <div className="absolute bottom-0 left-0 p-10">
                    <h4 className="text-heading-section text-white font-open-sans-hebrew font-bold mb-3">
                      {app.title}
                    </h4>
                    <p className="text-body text-white/70">
                      {app.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* <div className="flex justify-end mt-8 gap-4">
              <button className="w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center hover:bg-black hover:text-white transition-all text-gray-400">
                <ArrowRight size={20} className="rotate-180" />
              </button>
              <button className="w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center hover:bg-black hover:text-white transition-all text-gray-900">
                <ArrowRight size={20} />
              </button>
            </div> */}
          </div>
      </div>
    </div>
  );
};
